import { RewarePublicTypes } from '@troverecommerce/shared-rest-api';
import { exec } from '../utils';

// The fields this mutation actually selects. Narrower than the full `UserPublic` (whose other
// fields are never populated here), so callers can't read an unselected field expecting a value.
export type MagicLinkSessionUser = Pick<
  RewarePublicTypes.UserPublic,
  'uuid' | 'name' | 'email' | 'oneTimeToken' | 'token'
>;

/**
 * Exchanges a single-use magic-link credential for a dashboard session (JWT + one-time token on
 * the returned user), matching the shape returned by the badge/SSO flows. Accepts either the link
 * `token` (clicked on the requesting device) or the `email` + typed `code` (confirmation form).
 */
export const authenticateFromMagicLink = async ({
  token,
  email,
  code,
}: RewarePublicTypes.PublicMutationsAuthenticateFromMagicLinkArgs): Promise<MagicLinkSessionUser> => {
  const query = `
    mutation authenticateFromMagicLink($token: String, $email: String, $code: String) {
      authenticateFromMagicLink(token: $token, email: $email, code: $code) {
        uuid
        name
        email
        oneTimeToken
        token
      }
    }
  `;

  const data = await exec<
    RewarePublicTypes.PublicMutationsAuthenticateFromMagicLinkArgs,
    Pick<RewarePublicTypes.PublicMutations, 'authenticateFromMagicLink'>
  >(query, { variables: { token, email, code } });

  // The field is nullable in the schema, but a successful mutation always returns the user
  // (failures surface as GraphQL errors, which `exec` throws).
  const user = data.authenticateFromMagicLink;
  if (!user) {
    throw new Error('authenticateFromMagicLink returned no user');
  }

  return user;
};
